import './template.css'
import { useState, useContext, createContext, useEffect } from 'react'
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Circle,
  Settings,
  X,
  Plus,
  Trash2,
  Eye,
  Save,
  Upload,
} from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useAppServices } from 'hook/services'
import Swal from 'sweetalert2'

// Create context for form state management
const FormBuilderContext = createContext()

// Main component
export default function StepperFormBuilder() {
  // State for tracking if we're in build mode or preview mode
  const [mode, setMode] = useState('build') // "build" or "preview"

  // Store form configuration
  const [formConfig, setFormConfig] = useState({
    title: 'Multi-Step Form',
    description: 'Complete all steps to submit your information',
    primaryColor: '#4f46e5', // Indigo-600
    secondaryColor: '#818cf8', // Indigo-400
    fontColor: '#000',
    cardBackgroundColor: '#ffffff',
    steps: [
      {
        id: 1,
        title: 'Personal Information',
        description: 'Tell us about yourself',
        fields: [
          {
            id: '1-1',
            type: 'text',
            label: 'Full Name',
            placeholder: 'Enter your full name',
            required: true,
          },
          {
            id: '1-2',
            type: 'email',
            label: 'Email Address',
            placeholder: 'Enter your email',
            required: true,
          },
        ],
      },
      {
        id: 2,
        title: 'Contact Details',
        description: 'How can we reach you?',
        fields: [
          {
            id: '2-1',
            type: 'text',
            label: 'Phone Number',
            placeholder: 'Enter your phone number',
            required: false,
          },
          {
            id: '2-2',
            type: 'text',
            label: 'Address',
            placeholder: 'Enter your address',
            required: false,
          },
        ],
      },
    ],
  })

  // Store current form data (for preview mode)
  const [formData, setFormData] = useState({})

  // Track current step
  const [currentStep, setCurrentStep] = useState(1)

  // Track selected step/field for editing
  const [selectedStep, setSelectedStep] = useState(1)
  const [selectedField, setSelectedField] = useState(null)
  const [processing, setProcessing] = useState(false)

  const { company_id, app_id, id } = useParams()

  // Context value to share with child components
  const contextValue = {
    formConfig,
    setFormConfig,
    formData,
    setFormData,
    currentStep,
    setCurrentStep,
    selectedStep,
    setSelectedStep,
    selectedField,
    setSelectedField,
    mode,
  }

  // Toggle between build and preview modes
  const toggleMode = () => {
    setMode(mode === 'build' ? 'preview' : 'build')
    setCurrentStep(1)
    setFormData({})
  }

  // Export form configuration as JSON
  // const exportConfig = () => {
  //   const dataStr =
  //     'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(formConfig, null, 2))
  //   const downloadAnchorNode = document.createElement('a')
  //   downloadAnchorNode.setAttribute('href', dataStr)
  //   downloadAnchorNode.setAttribute('download', 'form-config.json')
  //   document.body.appendChild(downloadAnchorNode)
  //   downloadAnchorNode.click()
  //   downloadAnchorNode.remove()
  // }

  const Service = useAppServices()
  const [design, setDesign] = useState(null)
  const [loading, setLoading] = useState(false)

  const getDesign = async () => {
    setLoading(true)
    const { response } = await Service.servey.get_levelupstore({
      query: `form_id=${id}`,
    })
    setLoading(false)

    if (response?.data) {
      setDesign(response.data)
    }

    if (response?.data?.design) {
      setFormConfig({ ...response.data?.design })
    }
  }

  useEffect(async () => {
    getDesign()
  }, [])

  const exportConfig = async () => {
    setProcessing(true)
    const payload = {
      design: formConfig,
      company_id,
      app_id,
    }

    if (design?._id) payload._id = design._id

    const { response } = await Service.servey[design?._id ? 'update_levelupstore' : 'levelupstore'](
      { payload }
    )
    Swal.fire({
      icon: 'success',
      title: 'Design Saved Successfully',
      showConfirmButton: false,
      timer: 1500,
    })
    setProcessing(false)
  }

  return (
    <div id="templateDesign">
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
          }}
        >
          Loading...
        </div>
      ) : (
        <FormBuilderContext.Provider value={contextValue}>
          <div className="w-full max-w-full mx-auto p-4 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'build' ? 'Form Builder' : 'Form Preview'}
              </h1>
              <div className="flex gap-2">
                <button
                  onClick={toggleMode}
                  className="flex items-center gap-1 px-3 py-2 bg-white border rounded-md shadow-sm hover:bg-gray-50"
                >
                  {mode === 'build' ? <Eye size={16} /> : <Settings size={16} />}
                  {mode === 'build' ? 'Preview' : 'Edit'}
                </button>
                {mode === 'build' && (
                  <button
                    onClick={exportConfig}
                    className="flex items-center gap-1 px-3 py-2 bg-white border rounded-md shadow-sm hover:bg-gray-50"
                  >
                    <Save size={16} />
                    Save
                  </button>
                )}
              </div>
            </div>

            {mode === 'build' ? <FormBuilder /> : <FormPreview />}
          </div>
        </FormBuilderContext.Provider>
      )}
    </div>
  )
}

// Form Builder Mode Component
function FormBuilder() {
  const {
    formConfig,
    setFormConfig,
    selectedStep,
    setSelectedStep,
    selectedField,
    setSelectedField,
    formData,
    setFormData,
    currentStep,
    setCurrentStep,
  } = useContext(FormBuilderContext);
  const params = useParams();
  const Service = useAppServices();
  const [customFields, setCustomFields] = useState([]);
  const [activeTab, setActiveTab] = useState('field');
  const totalSteps = formConfig.steps.length;
  const [validationErrors, setValidationErrors] = useState({});

  // Add a new step
  const addStep = () => {
    const newStepId =
      formConfig.steps.length > 0 ? Math.max(...formConfig.steps.map((s) => s.id)) + 1 : 1;

    const newStep = {
      id: newStepId,
      title: `Step ${newStepId}`,
      description: 'Step description',
      fields: [],
    };

    setFormConfig({
      ...formConfig,
      steps: [...formConfig.steps, newStep],
    });

    setSelectedStep(newStepId);
    setSelectedField(null);
  };

  // Delete a step
  const deleteStep = (stepId) => {
    const updatedSteps = formConfig.steps.filter((step) => step.id !== stepId);

    setFormConfig({
      ...formConfig,
      steps: updatedSteps,
    });

    if (selectedStep === stepId) {
      setSelectedStep(updatedSteps.length > 0 ? updatedSteps[0].id : null);
      setSelectedField(null);
    }
  };

  // Add field to current step
  const addField = () => {
    const currentStepIndex = formConfig.steps.findIndex((step) => step.id === selectedStep);
    if (currentStepIndex === -1) return;

    const currentStep = formConfig.steps[currentStepIndex];
    const newFieldId =
      currentStep.fields.length > 0
        ? `${selectedStep}-${currentStep.fields.length + 1}`
        : `${selectedStep}-1`;

    const newField = {
      id: newFieldId,
      type: 'text',
      label: 'New Field',
      placeholder: 'Enter value',
      required: false,
    };

    const updatedSteps = [...formConfig.steps];
    updatedSteps[currentStepIndex] = {
      ...currentStep,
      fields: [...currentStep.fields, newField],
    };

    setFormConfig({
      ...formConfig,
      steps: updatedSteps,
    });

    setSelectedField(newFieldId);
  };

  // Delete a field
  const deleteField = (fieldId) => {
    const currentStepIndex = formConfig.steps.findIndex((step) => step.id === selectedStep);
    if (currentStepIndex === -1) return;

    const currentStep = formConfig.steps[currentStepIndex];
    const updatedFields = currentStep.fields.filter((field) => field.id !== fieldId);

    const updatedSteps = [...formConfig.steps];
    updatedSteps[currentStepIndex] = {
      ...currentStep,
      fields: updatedFields,
    };

    setFormConfig({
      ...formConfig,
      steps: updatedSteps,
    });

    if (selectedField === fieldId) {
      setSelectedField(null);
    }
  };

  // Move to next step
  const nextStep = () => {
    if (!validateStep()) return;

    const currentIndex = formConfig.steps.findIndex((step) => step.id === currentStep);
    if (currentIndex < formConfig.steps.length - 1) {
      const nextStepId = formConfig.steps[currentIndex + 1].id;
      setCurrentStep(nextStepId);
      setSelectedStep(nextStepId);
      setSelectedField(null);
    }
  };

  // Move to previous step
  const prevStep = () => {
    const currentIndex = formConfig.steps.findIndex((step) => step.id === currentStep);
    if (currentIndex > 0) {
      const prevStepId = formConfig.steps[currentIndex - 1].id;
      setCurrentStep(prevStepId);
      setSelectedStep(prevStepId);
      setSelectedField(null);
    }
  };

  // Update form settings
  const updateFormSettings = (property, value) => {
    setFormConfig({
      ...formConfig,
      [property]: value,
    });
  };

  // Update step properties
  const updateStepProperties = (property, value) => {
    const stepIndex = formConfig.steps.findIndex((step) => step.id === selectedStep);
    if (stepIndex === -1) return;

    const updatedSteps = [...formConfig.steps];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      [property]: value,
    };

    setFormConfig({
      ...formConfig,
      steps: updatedSteps,
    });
  };

  // Update field properties
  const updateFieldProperties = (property, value) => {
    if (!selectedField) return;

    const stepIndex = formConfig.steps.findIndex((step) => step.id === selectedStep);
    if (stepIndex === -1) return;

    const fieldIndex = formConfig.steps[stepIndex].fields.findIndex(
      (field) => field.id === selectedField
    );
    if (fieldIndex === -1) return;

    const updatedSteps = [...formConfig.steps];
    const oldField = updatedSteps[stepIndex].fields[fieldIndex];

    if (property === 'id') {
      // Check for duplicate IDs
      const isDuplicate = updatedSteps[stepIndex].fields.some(
        (field) => field.id === value && field.id !== oldField.id
      );
      if (isDuplicate) {
        alert('Field ID must be unique within the step.');
        return;
      }
      if (!value.trim()) {
        alert('Field ID cannot be empty.');
        return;
      }

      // Update field ID
      updatedSteps[stepIndex].fields[fieldIndex] = {
        ...oldField,
        id: value,
      };

      // Update formData to reflect new ID
      const newFormData = { ...formData };
      if (oldField.id in newFormData) {
        newFormData[value] = newFormData[oldField.id];
        delete newFormData[oldField.id];
      }
      setFormData(newFormData);

      // Update selectedField to new ID
      setSelectedField(value);
    } else {
      // Handle other properties
      updatedSteps[stepIndex].fields[fieldIndex] = {
        ...oldField,
        [property]: value,
      };
    }

    setFormConfig({
      ...formConfig,
      steps: updatedSteps,
    });
  };

  // Get current step object for preview
  const currentStepObject = formConfig.steps.find((step) => step.id === selectedStep) || {};

  // Get current field object
  const currentField = selectedField
    ? currentStepObject.fields?.find((field) => field.id === selectedField)
    : null;

  const getCustomValues = async (locationId) => {
    const { response } = await Service.servey.config_data({
      query: `locationId=${locationId}&company_id=${params.company_id}&app_id=${params.app_id}`,
    });
    if (response) {
      const customFields = response.data.map((field) => {
        return {
          key: field.fieldKey,
          label: field.name,
          description: field.description,
          value: field.id,
          ...field,
        };
      });
      setCustomFields(customFields);
    }
    return;
  };

  const getSurvey = async () => {
    const { response } = await Service.servey.get_levelupstore({
      query: `form_id=${params.id}`,
    });
    if (response) {
      await getCustomValues(response.data?.location_id);
    }
  };

  // Validate current step
  const validateStep = () => {
    const stepObj = formConfig.steps.find((step) => step.id === currentStep);
    if (!stepObj) return true;

    const errors = {};
    let isValid = true;

    stepObj.fields.forEach((field) => {
      if (field.required && (!formData[field.id] || formData[field.id].trim() === '')) {
        errors[field.id] = `${field.label} is required`;
        isValid = false;
      }

      if (
        field.type === 'email' &&
        formData[field.id] &&
        !formData[field.id].match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
      ) {
        errors[field.id] = 'Please enter a valid email address';
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Handle field changes
  const handleChange = (fieldId, value) => {
    setFormData({
      ...formData,
      [fieldId]: value,
    });

    if (validationErrors[fieldId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[fieldId];
      setValidationErrors(newErrors);
    }
  };

  // Render form field based on type
  const renderField = (field) => {
    const commonProps = {
      id: field.id,
      value: formData[field.id] || '',
      onChange: (e) => handleChange(field.id, e.target.value),
      placeholder: field.placeholder,
      className: `block w-full p-2 border rounded-md ${
        validationErrors[field.id] ? 'border-red-500' : 'border-gray-300'
      }`,
      onClick: () => setSelectedField(field.id),
      style: {
        color: formConfig.fontColor || '#000000',
      },
    };

    switch (field.type) {
      case 'textarea':
        return <textarea {...commonProps} rows={4} />;

      case 'select':
        return (
          <select {...commonProps} value={formData[field.id] || ''}>
            <option value="">Select an option</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            )) || []}
          </select>
        );

      case 'checkbox':
        return (
          <div className="space-y-2" onClick={() => setSelectedField(field.id)}>
            {field.options?.map((option, idx) => {
              const checkboxId = `${field.id}-${idx}`;
              const isChecked = formData[field.id]?.includes(option) || false;

              return (
                <div key={checkboxId} className="flex items-center">
                  <input
                    type="checkbox"
                    id={checkboxId}
                    checked={isChecked}
                    onChange={(e) => {
                      const currentValues = formData[field.id] ? [...formData[field.id]] : [];
                      if (e.target.checked) {
                        handleChange(field.id, [...currentValues, option]);
                      } else {
                        handleChange(
                          field.id,
                          currentValues.filter((val) => val !== option)
                        );
                      }
                    }}
                    className="h-4 w-4 border-gray-300 rounded text-indigo-600"
                  />
                  <label
                    htmlFor={checkboxId}
                    className="ml-2 block text-sm"
                    style={{
                      color: formConfig.fontColor || '#000000',
                    }}
                  >
                    {option}
                  </label>
                </div>
              );
            }) || []}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2" onClick={() => setSelectedField(field.id)}>
            {field.options?.map((option, idx) => {
              const radioId = `${field.id}-${idx}`;

              return (
                <div key={radioId} className="flex items-center">
                  <input
                    type="radio"
                    id={radioId}
                    name={field.id}
                    value={option}
                    checked={formData[field.id] === option}
                    onChange={() => handleChange(field.id, option)}
                    className="h-4 w-4 border-gray-300 text-indigo-600"
                  />
                  <label
                    htmlFor={radioId}
                    className="ml-2 block text-sm"
                    style={{
                      color: formConfig.fontColor || '#000000',
                    }}
                  >
                    {option}
                  </label>
                </div>
              );
            }) || []}
          </div>
        );

      case 'date':
        return <input type="date" {...commonProps} />;

      default:
        return <input type={field.type} {...commonProps} />;
    }
  };

  useEffect(() => {
    getSurvey();
  }, []);

  return (
    <div className="w-full px-4">
      <div className="grid grid-cols-12 gap-6">
        {/* Left sidebar - Steps management */}
        <div className="col-span-3 bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-700">Steps</h2>
            <button
              onClick={addStep}
              className="p-1 rounded-full hover:bg-gray-100"
              title="Add new step"
            >
              <Plus size={18} />
            </button>
          </div>

          <div className="space-y-2">
            {formConfig.steps.map((step) => (
              <div
                key={step.id}
                className={`p-2 rounded-md cursor-pointer flex justify-between items-center ${
                  selectedStep === step.id
                    ? 'bg-gray-100 border border-gray-300'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  setSelectedStep(step.id);
                  setSelectedField(null);
                  setActiveTab('step');
                }}
              >
                <div className="truncate">
                  <span className="mr-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-xs">
                    {step.id}
                  </span>
                  {step.title}
                </div>
                {formConfig.steps.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteStep(step.id);
                    }}
                    className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                    title="Delete step"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Middle - Form preview for selected step */}
        <div
          className="col-span-5 p-4 rounded-lg shadow-sm border"
          style={{ backgroundColor: formConfig.cardBackgroundColor || '#ffffff' }}
        >
          {selectedStep ? (
            <div className="max-w-2xl mx-auto">
              <div className="mb-6 text-center">
                <h2
                  className="text-2xl font-bold"
                  style={{
                    color: formConfig.primaryColor || '#000000',
                  }}
                >
                  {formConfig.title}
                </h2>
                <p
                  className="mt-1"
                  style={{
                    color: formConfig.fontColor || '#000000',
                  }}
                >
                  {formConfig.description}
                </p>
              </div>

              {/* Progress indicator */}
              <div className="mb-8">
                <div className="flex justify-between">
                  {formConfig.steps.map((step, idx) => (
                    <div
                      key={step.id}
                      className="flex flex-col items-center"
                      style={{ width: `${100 / totalSteps}%` }}
                    >
                      <div className="flex items-center">
                        {idx > 0 && (
                          <div
                            className="h-1 flex-1"
                            style={{
                              backgroundColor:
                                idx < formConfig.steps.findIndex((s) => s.id === currentStep) + 1
                                  ? formConfig.primaryColor
                                  : '#e5e7eb',
                            }}
                          />
                        )}
                        <div
                          className="flex items-center justify-center h-8 w-8 rounded-full"
                          style={{
                            backgroundColor:
                              idx < formConfig.steps.findIndex((s) => s.id === currentStep) + 1
                                ? formConfig.primaryColor
                                : idx === formConfig.steps.findIndex((s) => s.id === currentStep)
                                ? formConfig.secondaryColor
                                : '#e5e7eb',
                            color:
                              idx <= formConfig.steps.findIndex((s) => s.id === currentStep)
                                ? 'white'
                                : formConfig.secondaryColor || '#6b7280',
                          }}
                        >
                          {idx < formConfig.steps.findIndex((s) => s.id === currentStep) ? (
                            <CheckCircle size={16} />
                          ) : (
                            <span className="text-xs">{idx + 1}</span>
                          )}
                        </div>
                        {idx < formConfig.steps.length - 1 && (
                          <div
                            className="h-1 flex-1"
                            style={{
                              backgroundColor:
                                idx < formConfig.steps.findIndex((s) => s.id === currentStep)
                                  ? formConfig.primaryColor
                                  : '#e5e7eb',
                            }}
                          />
                        )}
                      </div>
                      <span
                        className="text-xs mt-1 text-center"
                        style={{
                          color:
                            idx === formConfig.steps.findIndex((s) => s.id === currentStep)
                              ? formConfig.primaryColor
                              : formConfig.secondaryColor || '#6b7280',
                        }}
                      >
                        {step.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step content */}
              <div className="mb-6">
                <h3
                  className="text-lg font-semibold mb-1"
                  style={{
                    color: formConfig.fontColor || '#000000',
                  }}
                >
                  {currentStepObject.title}
                </h3>
                <p
                  className="mb-4"
                  style={{
                    color: formConfig.fontColor || '#000000',
                  }}
                >
                  {currentStepObject.description}
                </p>

                <div className="space-y-4">
                  {currentStepObject.fields.map((field) => (
                    <div key={field.id} className="form-group">
                      <label
                        htmlFor={field.id}
                        className="block text-sm font-medium mb-1"
                        style={{
                          color: formConfig.fontColor || '#000000',
                        }}
                      >
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteField(field.id);
                          }}
                          className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                          title="Delete field"
                        >
                          <Trash2 size={14} />
                        </button>
                      </label>

                      {renderField(field)}

                      {validationErrors[field.id] && (
                        <p className="mt-1 text-sm text-red-500">{validationErrors[field.id]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === formConfig.steps[0]?.id}
                  className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                    currentStep === formConfig.steps[0]?.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'hover:bg-gray-50'
                  }`}
                  style={{
                    borderColor:
                      currentStep === formConfig.steps[0]?.id
                        ? 'transparent'
                        : formConfig.secondaryColor || '#d1d5db',
                    color:
                      currentStep === formConfig.steps[0]?.id
                        ? '#9ca3af'
                        : formConfig.secondaryColor || '#374151',
                    backgroundColor:
                      currentStep === formConfig.steps[0]?.id ? '#f3f4f6' : 'transparent',
                  }}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                {currentStep === formConfig.steps[formConfig.steps.length - 1]?.id ? (
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-md text-white flex items-center gap-1"
                    style={{ backgroundColor: formConfig.primaryColor }}
                  >
                    Submit
                    <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-4 py-2 rounded-md text-white flex items-center gap-1"
                    style={{ backgroundColor: formConfig.primaryColor }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <p
                className="mb-4"
                style={{
                  color: formConfig.fontColor || '#000000',
                }}
              >
                No step selected
              </p>
              <button
                onClick={addStep}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Add your first step
              </button>
            </div>
          )}
        </div>

        {/* Right panel - Properties */}
        <div className="col-span-4 bg-white p-4 rounded-lg shadow-sm border">
          <h2 className="font-semibold text-gray-700 mb-4">Properties</h2>
          <div className="mb-6">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'field'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => {
                  setActiveTab('field');
                  if (!selectedStep && formConfig.steps.length > 0) {
                    setSelectedStep(formConfig.steps[0].id);
                  }
                }}
                disabled={!selectedStep}
              >
                Field Settings
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'step'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => {
                  setActiveTab('step');
                  setSelectedField(null);
                  if (!selectedStep && formConfig.steps.length > 0) {
                    setSelectedStep(formConfig.steps[0].id);
                  }
                }}
                disabled={!formConfig.steps.length}
              >
                Form Settings
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'form'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => {
                  setActiveTab('form');
                  setSelectedField(null);
                  if (formConfig.steps.length > 0) {
                    setSelectedStep(formConfig.steps[0].id);
                  }
                }}
              >
                Branding
              </button>
            </div>
          </div>
          {activeTab === 'form' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formConfig.primaryColor || '#4f46e5'}
                    onChange={(e) => updateFormSettings('primaryColor', e.target.value)}
                    className="h-8 w-8 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formConfig.primaryColor || '#4f46e5'}
                    onChange={(e) => updateFormSettings('primaryColor', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formConfig.secondaryColor || '#818cf8'}
                    onChange={(e) => updateFormSettings('secondaryColor', e.target.value)}
                    className="h-8 w-8 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formConfig.secondaryColor || '#818cf8'}
                    onChange={(e) => updateFormSettings('secondaryColor', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formConfig.fontColor || '#000000'}
                    onChange={(e) => updateFormSettings('fontColor', e.target.value)}
                    className="h-8 w-8 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formConfig.fontColor || '#000000'}
                    onChange={(e) => updateFormSettings('fontColor', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card Background Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formConfig.cardBackgroundColor || '#ffffff'}
                    onChange={(e) => updateFormSettings('cardBackgroundColor', e.target.value)}
                    className="h-8 w-8 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formConfig.cardBackgroundColor || '#ffffff'}
                    onChange={(e) => updateFormSettings('cardBackgroundColor', e.target.value)}
                    className="block w-full border border-gray-300 rounded-md p-2"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'step' && selectedStep && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Form Title</label>
                <input
                  type="text"
                  value={formConfig.title || ''}
                  onChange={(e) => updateFormSettings('title', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Form Description
                </label>
                <textarea
                  value={formConfig.description || ''}
                  onChange={(e) => updateFormSettings('description', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md p-2"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Title</label>
                <input
                  type="text"
                  value={currentStepObject.title || ''}
                  onChange={(e) => updateStepProperties('title', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Step Description
                </label>
                <input
                  type="text"
                  value={currentStepObject.description || ''}
                  onChange={(e) => updateStepProperties('description', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md p-2"
                />
              </div>
            </div>
          )}

          {activeTab === 'field' && selectedStep && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">Fields</h3>
                <button
                  onClick={addField}
                  className="p-1 rounded-full hover:bg-gray-100"
                  title="Add new field"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="space-y-2">
                {/* {currentStepObject.fields?.map((field) => (
                  <div
                    key={field.id}
                    className={`p-2 rounded-md cursor-pointer flex justify-between items-center ${
                      selectedField === field.id
                        ? 'bg-gray-100 border border-gray-300'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedField(field.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{field.type}</span>
                      <span className="truncate">{field.label}</span>
                      {field.required && <span className="text-red-500 text-xs">*</span>}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteField(field.id);
                      }}
                      className="p-1 rounded-full hover:bg-gray-200 text-gray-500"
                      title="Delete field"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  ))}
                  {currentStepObject.fields?.length === 0 && (
                  <p className="text-sm text-gray-500 text-center p-4">
                    No fields added yet. Click the + button to add fields.
                  </p>
                  )} */}
              </div>
              {selectedField && (
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Type
                    </label>
                    <select
                      value={currentField?.type || 'text'}
                      onChange={(e) => updateFieldProperties('type', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    >
                      <option value="text">Text</option>
                      <option value="email">Email</option>
                      <option value="password">Password</option>
                      <option value="number">Number</option>
                      <option value="tel">Telephone</option>
                      <option value="textarea">Text Area</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="radio">Radio Buttons</option>
                      <option value="date">Date</option>
                      <option value="time">Time</option>
                      <option value="datetime-local">Date & time</option>
                      <option value="color">Color</option>
                      <option value="file">File</option>
                      <option value="url">URL</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Value
                    </label>
                    <select
                      value={currentField?.name || ''}
                      onChange={(e) => {
                        const field = customFields.find((item) => item.key === e.target.value);
                        if (field.value) updateFieldProperties('value', field.value);
                        updateFieldProperties('name', e.target.value);
                      }}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    >
                      <option value={''}>Select field</option>
                      {customFields.map((field) => (
                        <option key={field.key} value={field.key}>
                          {field.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                    <input
                      type="text"
                      value={currentField?.label || ''}
                      onChange={(e) => updateFieldProperties('label', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={currentField?.placeholder || ''}
                      onChange={(e) => updateFieldProperties('placeholder', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={currentField?.name || ''}
                      onChange={(e) => updateFieldProperties('name', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                    <input
                      type="text"
                      value={currentField?.id || ''}
                      onChange={(e) => updateFieldProperties('id', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md p-2"
                    />
                  </div>
                  {['select', 'checkbox', 'radio'].includes(currentField?.type) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Options (comma separated)
                      </label>
                      <input
                        type="text"
                        value={currentField?.options?.join(', ') || ''}
                        onChange={(e) => {
                          const options = e.target.value
                            .split(',')
                            .map((o) => o.trim())
                            .filter(Boolean);
                          updateFieldProperties('options', options);
                        }}
                        className="block w-full border border-gray-300 rounded-md p-2"
                      />
                    </div>
                  )}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="required"
                      checked={currentField?.required || false}
                      onChange={(e) => updateFieldProperties('required', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <label htmlFor="required" className="ml-2 block text-sm text-gray-700">
                      Required
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Form Preview Mode Component
function FormPreview() {
  const { formConfig, formData, setFormData, currentStep, setCurrentStep } =
    useContext(FormBuilderContext);
  const totalSteps = formConfig.steps.length;

  // Find the current step object
  const stepObject = formConfig.steps.find((step) => step.id === currentStep) || formConfig.steps[0];

  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Validate current step
  const validateStep = () => {
    const currentStepObject = formConfig.steps.find((step) => step.id === currentStep);
    if (!currentStepObject) return true;

    const errors = {};
    let isValid = true;

    currentStepObject.fields.forEach((field) => {
      if (field.required && (!formData[field.id] || formData[field.id].trim() === '')) {
        errors[field.id] = `${field.label} is required`;
        isValid = false;
      }

      if (
        field.type === 'email' &&
        formData[field.id] &&
        !formData[field.id].match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
      ) {
        errors[field.id] = 'Please enter a valid email address';
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Handle field changes
  const handleChange = (fieldId, value) => {
    setFormData({
      ...formData,
      [fieldId]: value,
    });

    // Clear validation error for this field if it exists
    if (validationErrors[fieldId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[fieldId];
      setValidationErrors(newErrors);
    }
  };

  // Move to next step
  const nextStep = () => {
    if (!validateStep()) return;

    const currentIndex = formConfig.steps.findIndex((step) => step.id === currentStep);
    if (currentIndex < formConfig.steps.length - 1) {
      setCurrentStep(formConfig.steps[currentIndex + 1].id);
    }
  };

  // Move to previous step
  const prevStep = () => {
    const currentIndex = formConfig.steps.findIndex((step) => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(formConfig.steps[currentIndex - 1].id);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateStep()) return;

    alert('Form submitted successfully!\n\n' + JSON.stringify(formData, null, 2));
  };

  // Render form field based on type
  const renderField = (field) => {
    const commonProps = {
      id: field.id,
      value: formData[field.id] || '',
      onChange: (e) => handleChange(field.id, e.target.value),
      placeholder: field.placeholder,
      className: `block w-full p-2 border rounded-md ${
        validationErrors[field.id] ? 'border-red-500' : 'border-gray-300'
      }`,
      style: {
        color: formConfig.fontColor || '#000000',
        borderColor: validationErrors[field.id]
          ? '#ef4444'
          : formConfig.secondaryColor || '#d1d5db',
      },
    };

    switch (field.type) {
      case 'textarea':
        return <textarea {...commonProps} rows={4} />;

      case 'select':
        return (
          <select {...commonProps} value={formData[field.id] || ''}>
            <option value="">Select an option</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            )) || []}
          </select>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option, idx) => {
              const checkboxId = `${field.id}-${idx}`;
              const isChecked = formData[field.id]?.includes(option) || false;

              return (
                <div key={checkboxId} className="flex items-center">
                  <input
                    type="checkbox"
                    id={checkboxId}
                    checked={isChecked}
                    onChange={(e) => {
                      const currentValues = formData[field.id] ? [...formData[field.id]] : [];
                      if (e.target.checked) {
                        handleChange(field.id, [...currentValues, option]);
                      } else {
                        handleChange(
                          field.id,
                          currentValues.filter((val) => val !== option)
                        );
                      }
                    }}
                    className="h-4 w-4 border-gray-300 rounded"
                    style={{
                      color: formConfig.primaryColor || '#4f46e5',
                      borderColor: formConfig.secondaryColor || '#d1d5db',
                    }}
                  />
                  <label
                    htmlFor={checkboxId}
                    className="ml-2 block text-sm"
                    style={{
                      color: formConfig.fontColor || '#000000',
                    }}
                  >
                    {option}
                  </label>
                </div>
              );
            }) || []}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option, idx) => {
              const radioId = `${field.id}-${idx}`;

              return (
                <div key={radioId} className="flex items-center">
                  <input
                    type="radio"
                    id={radioId}
                    name={field.id}
                    value={option}
                    checked={formData[field.id] === option}
                    onChange={() => handleChange(field.id, option)}
                    className="h-4 w-4 border-gray-300"
                    style={{
                      color: formConfig.primaryColor || '#4f46e5',
                      borderColor: formConfig.secondaryColor || '#d1d5db',
                    }}
                  />
                  <label
                    htmlFor={radioId}
                    className="ml-2 block text-sm"
                    style={{
                      color: formConfig.fontColor || '#000000',
                    }}
                  >
                    {option}
                  </label>
                </div>
              );
            }) || []}
          </div>
        );

      case 'date':
        return <input type="date" {...commonProps} />;

      default:
        return <input type={field.type} {...commonProps} />;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div
        className="rounded-lg shadow-sm border p-6"
        style={{ backgroundColor: formConfig.cardBackgroundColor || '#ffffff' }}
      >
        <div className="mb-6 text-center">
          <h2
            className="text-2xl font-bold"
            style={{ color: formConfig.primaryColor || '#4f46e5' }}
          >
            {formConfig.title}
          </h2>
          <p style={{ color: formConfig.fontColor || '#000000' }}>{formConfig.description}</p>
        </div>

        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex justify-between">
            {formConfig.steps.map((step, idx) => (
              <div
                key={step.id}
                className="flex flex-col items-center"
                style={{ width: `${100 / totalSteps}%` }}
              >
                <div className="flex items-center">
                  {idx > 0 && (
                    <div
                      className="h-1 flex-1"
                      style={{
                        backgroundColor:
                          idx < formConfig.steps.findIndex((s) => s.id === currentStep) + 1
                            ? formConfig.primaryColor
                            : '#e5e7eb',
                      }}
                    />
                  )}
                  <div
                    className="flex items-center justify-center h-8 w-8 rounded-full"
                    style={{
                      backgroundColor:
                        idx < formConfig.steps.findIndex((s) => s.id === currentStep) + 1
                          ? formConfig.primaryColor
                          : idx === formConfig.steps.findIndex((s) => s.id === currentStep)
                          ? formConfig.secondaryColor
                          : '#e5e7eb',
                      color:
                        idx <= formConfig.steps.findIndex((s) => s.id === currentStep)
                          ? '#ffffff'
                          : formConfig.fontColor || '#6b7280',
                    }}
                  >
                    {idx < formConfig.steps.findIndex((s) => s.id === currentStep) ? (
                      <CheckCircle size={16} />
                    ) : (
                      <span className="text-xs">{idx + 1}</span>
                    )}
                  </div>
                  {idx < formConfig.steps.length - 1 && (
                    <div
                      className="h-1 flex-1"
                      style={{
                        backgroundColor:
                          idx < formConfig.steps.findIndex((s) => s.id === currentStep)
                            ? formConfig.primaryColor
                            : '#e5e7eb',
                      }}
                    />
                  )}
                </div>
                <span
                  className="text-xs mt-1 text-center"
                  style={{
                    color:
                      idx === formConfig.steps.findIndex((s) => s.id === currentStep)
                        ? formConfig.primaryColor
                        : formConfig.fontColor || '#6b7280',
                  }}
                >
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="mb-6">
          <h3
            className="text-lg font-semibold mb-1"
            style={{ color: formConfig.primaryColor || '#4f46e5' }}
          >
            {stepObject.title}
          </h3>
          <p style={{ color: formConfig.fontColor || '#000000' }}>{stepObject.description}</p>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {stepObject.fields.map((field) => (
                <div key={field.id} className="form-group">
                  <label
                    htmlFor={field.id}
                    className="block text-sm font-medium mb-1"
                    style={{ color: formConfig.fontColor || '#000000' }}
                  >
                    {field.label}
                    {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                  </label>

                  {renderField(field)}

                  {validationErrors[field.id] && (
                    <p className="mt-1 text-sm" style={{ color: '#ef4444' }}>
                      {validationErrors[field.id]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={prevStep}
                className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                  currentStep === formConfig.steps[0].id
                    ? 'invisible'
                    : 'hover:bg-gray-50'
                }`}
                style={{
                  borderColor: formConfig.secondaryColor || '#d1d5db',
                  color: formConfig.secondaryColor || '#374151',
                  backgroundColor: 'transparent',
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              {currentStep === formConfig.steps[formConfig.steps.length - 1].id ? (
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md text-white flex items-center gap-1"
                  style={{ backgroundColor: formConfig.primaryColor || '#4f46e5' }}
                >
                  Submit
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-4 py-2 rounded-md text-white flex items-center gap-1"
                  style={{ backgroundColor: formConfig.primaryColor || '#4f46e5' }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
